/**
 * ComposerStagingContext - state for things parked in the composer
 * waiting for the next send: image attachments, recorded voice
 * (transcribed but unsent), etc.
 *
 * Lives just above the Composer so the buttons (Attach, Mic) and
 * the Composer's textarea/send button all share it without
 * prop-drilling through the rail.
 *
 * D5a scope:
 *   - staged image (one at a time; the legacy supports multiple
 *     but the picker is single-file. If a future PR needs N
 *     images, swap `staged` to an array.)
 *   - upload status (idle / uploading / failed) so the staging
 *     UI can show a spinner and disable the send button until
 *     the upload completes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AttachmentMeta } from '../api/attachment';

export type StagingStatus = 'idle' | 'uploading' | 'ready' | 'failed';

interface StagedAttachment {
  meta: AttachmentMeta;
  /** Original-file name for display. May differ from
   *  meta.original_name if the upload renamed (it doesn't, but
   *  keep separated for clarity). */
  displayName: string;
}

interface ComposerStagingContextValue {
  staged: StagedAttachment | null;
  status: StagingStatus;
  errorMessage: string | null;
  setStaged: (next: StagedAttachment | null) => void;
  setStatus: (next: StagingStatus) => void;
  setErrorMessage: (next: string | null) => void;
  /** Convenience: clear everything (post-send). */
  clear: () => void;
}

const ComposerStagingContext = createContext<ComposerStagingContextValue | null>(
  null,
);

export function ComposerStagingProvider({ children }: { children: ReactNode }) {
  const [staged, setStaged] = useState<StagedAttachment | null>(null);
  const [status, setStatus] = useState<StagingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const clear = useCallback(() => {
    setStaged(null);
    setStatus('idle');
    setErrorMessage(null);
  }, []);

  const value = useMemo(
    () => ({
      staged,
      status,
      errorMessage,
      setStaged,
      setStatus,
      setErrorMessage,
      clear,
    }),
    [staged, status, errorMessage, clear],
  );

  return (
    <ComposerStagingContext.Provider value={value}>
      {children}
    </ComposerStagingContext.Provider>
  );
}

export function useComposerStaging(): ComposerStagingContextValue {
  const ctx = useContext(ComposerStagingContext);
  if (!ctx) {
    throw new Error(
      'useComposerStaging must be used inside <ComposerStagingProvider>',
    );
  }
  return ctx;
}
